import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";
import { addYears } from "../../../utils/addYears";
import { formatDateToString } from "../../../utils/formatDateToString";
import Button from "../../Button";
import EditableContentBlock from "../../EditableContentBlock";
import FormDateInput from "../../FormDateInput";
import FormTextInput from "../../FormTextInput";
import { EditGreyFleetFormValues } from "../EditGreyFleetFormValues";
import GreyFleetFormRadioGroup from "../GreyFleetFormRadioGroup";

type EditBreakdownCoverInformationFormProps = {
    initialValues: EditGreyFleetFormValues;
    methods: UseFormReturn<EditGreyFleetFormValues>;
    isLoading: boolean;
    onSubmit: (data: EditGreyFleetFormValues, callback: () => void) => void;
};

const driverHasBreakdownCoverOptions = [
    { label: "Yes", id: "true" },
    { label: "No", id: "false" },
];

const today = new Date();
const thirtyYearsFromToday = addYears(today, 30);

const EditBreakdownCoverInformationForm = ({
    initialValues,
    methods,
    isLoading,
    onSubmit,
}: EditBreakdownCoverInformationFormProps) => {
    const [hasBreakdownCover, setHasBreakdownCover] = useState<boolean>(
        initialValues.breakdownCover?.hasBreakdownCover ?? false
    );

    const handleDriverHasBreakdownCoverChange = (
        value: string,
        onChangeCallback: (value: boolean) => void
    ) => {
        if (value === "true" || value === "false")
            onChangeCallback(value === "true" ? true : false);
    };

    const handleFormSubmit = async (
        values: EditGreyFleetFormValues,
        setIsEdit: Dispatch<SetStateAction<boolean>>
    ) => {
        onSubmit(values, () => setIsEdit(false));
    };

    const hasBreakdownCoverWatch = useWatch({
        control: methods.control,
        name: "breakdownCover.hasBreakdownCover",
        defaultValue: initialValues.breakdownCover?.hasBreakdownCover,
    });

    useEffect(() => {
        if (hasBreakdownCoverWatch !== undefined) {
            setHasBreakdownCover(hasBreakdownCoverWatch);
        }
    }, [hasBreakdownCoverWatch]);

    const handleCancel = (setIsEdit: Dispatch<SetStateAction<boolean>>) => {
        methods.reset();
        setIsEdit(false);
    };

    return (
        <EditableContentBlock
            title="Breakdown Cover Information"
            onClosed={() => methods.reset()}
        >
            {({ isEdit, setIsEdit }) => (
                <>
                    {!isEdit && (
                        <div className="p-6 space-y-2">
                            <p>
                                <b>Breakdown cover:</b>{" "}
                                {initialValues.breakdownCover?.hasBreakdownCover
                                    ? "Yes"
                                    : "No"}
                            </p>
                            <p>
                                <b>Breakdown cover provider:</b>{" "}
                                {
                                    initialValues.breakdownCover
                                        ?.breakdownCoverProvider
                                }
                            </p>
                            <p>
                                <b>Breakdown cover policy number:</b>{" "}
                                {
                                    initialValues.breakdownCover
                                        ?.breakdownCoverPolicyNumber
                                }
                            </p>
                            <p>
                                <b>Breakdown cover start date:</b>{" "}
                                {initialValues.breakdownCover
                                    ?.breakdownCoverStartDate instanceof Date
                                    ? formatDateToString(
                                          initialValues.breakdownCover
                                              ?.breakdownCoverStartDate
                                      )
                                    : ""}
                            </p>
                            <p>
                                <b>Breakdown cover end date:</b>{" "}
                                {initialValues.breakdownCover
                                    ?.breakdownCoverEndDate instanceof Date
                                    ? formatDateToString(
                                          initialValues.breakdownCover
                                              ?.breakdownCoverEndDate
                                      )
                                    : ""}
                            </p>
                        </div>
                    )}
                    {isEdit && (
                        <div className="p-6">
                            <form
                                onSubmit={methods.handleSubmit((values) => {
                                    handleFormSubmit(values, setIsEdit);
                                })}
                            >
                                <div className="w-full">
                                    <h2 className="pb-2 text-sm">
                                        Driver has breakdown cover
                                    </h2>
                                    <div className="flex">
                                        <Controller
                                            name="breakdownCover.hasBreakdownCover"
                                            control={methods.control}
                                            render={({
                                                field: { onChange, value },
                                            }) => (
                                                <GreyFleetFormRadioGroup
                                                    value={value?.toString()}
                                                    options={
                                                        driverHasBreakdownCoverOptions
                                                    }
                                                    error={
                                                        methods.formState.errors
                                                            .breakdownCover
                                                            ?.hasBreakdownCover
                                                    }
                                                    onChange={(value) =>
                                                        handleDriverHasBreakdownCoverChange(
                                                            value,
                                                            onChange
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                    <FormTextInput
                                        label="Breakdown cover provider"
                                        required
                                        id="breakdownCoverProvider"
                                        placeholder="Enter insurance company name"
                                        disabled={!hasBreakdownCover}
                                        error={
                                            methods.formState.errors
                                                .breakdownCover
                                                ?.breakdownCoverProvider
                                        }
                                        autoFocus
                                        {...methods.register(
                                            "breakdownCover.breakdownCoverProvider"
                                        )}
                                    />
                                    <div className="py-2" />
                                    <FormTextInput
                                        label="Breakdown cover policy number"
                                        required
                                        id="breakdownCoverPolicyNumber"
                                        placeholder="Enter insurance policy number"
                                        disabled={!hasBreakdownCover}
                                        error={
                                            methods.formState.errors
                                                .breakdownCover
                                                ?.breakdownCoverPolicyNumber
                                        }
                                        {...methods.register(
                                            "breakdownCover.breakdownCoverPolicyNumber"
                                        )}
                                    />
                                    <div className="py-2" />
                                    <Controller
                                        name="breakdownCover.breakdownCoverStartDate"
                                        control={methods.control}
                                        render={({
                                            field: { onChange, value },
                                        }) => (
                                            <FormDateInput
                                                id="breakdownCoverStartDate"
                                                label="Breakdown cover start date"
                                                disabled={!hasBreakdownCover}
                                                required
                                                value={value}
                                                onChange={onChange}
                                                error={
                                                    methods.formState.errors
                                                        .breakdownCover
                                                        ?.breakdownCoverStartDate
                                                }
                                                maxDate={new Date()}
                                            />
                                        )}
                                    />
                                    <div className="py-2" />
                                    <Controller
                                        name="breakdownCover.breakdownCoverEndDate"
                                        control={methods.control}
                                        render={({
                                            field: { onChange, value },
                                        }) => (
                                            <FormDateInput
                                                id="breakdownCoverEndDate"
                                                label="Breakdown cover end date"
                                                disabled={!hasBreakdownCover}
                                                value={value}
                                                onChange={onChange}
                                                required
                                                error={
                                                    methods.formState.errors
                                                        .breakdownCover
                                                        ?.breakdownCoverEndDate
                                                }
                                                maxDate={thirtyYearsFromToday}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex justify-between mt-8">
                                    <Button
                                        variant="tertiary"
                                        type="button"
                                        onClick={() => handleCancel(setIsEdit)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit" loading={isLoading}>
                                        Save
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}
                </>
            )}
        </EditableContentBlock>
    );
};

export default EditBreakdownCoverInformationForm;
